import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  Pagination,
  useTheme,
  Avatar,
  Stack,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { format, addDays, startOfWeek, endOfWeek } from "date-fns";
import { useSnackbar } from "notistack";
import { useUserProfile } from "../../../hooks/userProfile";
import TaskDetailsModal from "../../PopupModal/TaskDetailsModal";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import { QK } from "../../../base/qk";
import { getVisitTimeline } from "../../../api/services/visitplan";
import { deleteScheduleJob } from "../../../api/services/scheduling";
import JobEditModal from "../../../screens/VisitTimelineScreen/JobAction/JobEditModal";
import ScheduleFilter from "../../ClientList/ClientSchedulesDrawer/ScheduleFilter";
import ScheduleList from "../../ClientList/ClientSchedulesDrawer/ScheduleList";
import EmptyState from "../../ClientList/ClientSchedulesDrawer/EmptyState";

const ScheduleCardSkeleton = ({ theme }) => (
  <Box
    sx={{
      p: 2,
      mb: 1.5,
      borderRadius: 2.5,
      border: "1px solid #F2F4F7",
      bgcolor: "#FFFFFF",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.15),
      },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
      <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 1 }} />
      <Skeleton variant="text" width={100} height={16} />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 10 }} />
    </Stack>

    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.75 }} />

    <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Skeleton variant="circular" width={14} height={14} />
        <Skeleton variant="text" width={90} height={14} />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Skeleton variant="circular" width={14} height={14} />
        <Skeleton variant="text" width={80} height={14} />
      </Stack>
    </Stack>

    <Skeleton variant="rectangular" height={1} sx={{ mb: 1.25 }} />

    <Stack direction="row" alignItems="center" spacing={1}>
      <Stack direction="row" spacing={-0.75}>
        <Skeleton variant="circular" width={26} height={26} />
        <Skeleton variant="circular" width={26} height={26} />
      </Stack>
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.25 }} />
      <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.25 }} />
      <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.25 }} />
    </Stack>
  </Box>
);

const ScheduleFilterSkeleton = () => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{ mb: 2, flexWrap: "wrap", gap: 1.5 }}
  >
    <Skeleton variant="rounded" width={200} height={38} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rounded" width={130} height={38} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rounded" width={140} height={38} sx={{ borderRadius: 2 }} />
    <Box sx={{ flex: 1 }} />
    <Skeleton variant="rounded" width={90} height={38} sx={{ borderRadius: 2 }} />
  </Stack>
);

const ClientSchedulesDrawer = ({ open, clientId, onClose }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const profile = useUserProfile();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("today");
  const [status, setStatus] = useState("");
  const [location, setLocation] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditTimeModal, setShowEditTimeModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const today = new Date();
  let fromDate = "";
  let toDate = "";
  let dateParam = "";

  switch (filter) {
    case "today":
      fromDate = format(today, "yyyy-MM-dd");
      toDate = format(today, "yyyy-MM-dd");
      dateParam = format(today, "yyyy-MM-dd");
      break;
    case "yesterday": {
      const yesterday = addDays(today, -1);
      fromDate = format(yesterday, "yyyy-MM-dd");
      toDate = format(yesterday, "yyyy-MM-dd");
      dateParam = format(yesterday, "yyyy-MM-dd");
      break;
    }
    case "tomorrow": {
      const tomorrow = addDays(today, 1);
      fromDate = format(tomorrow, "yyyy-MM-dd");
      toDate = format(tomorrow, "yyyy-MM-dd");
      dateParam = format(tomorrow, "yyyy-MM-dd");
      break;
    }
    case "week1": {
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      fromDate = format(weekStart, "yyyy-MM-dd");
      toDate = format(weekEnd, "yyyy-MM-dd");
      dateParam = "";
      break;
    }
    case "week2": {
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const week2Start = addDays(weekStart, 7);
      const week2End = addDays(week2Start, 6);
      fromDate = format(week2Start, "yyyy-MM-dd");
      toDate = format(week2End, "yyyy-MM-dd");
      dateParam = "";
      break;
    }
    default:
      break;
  }

  const { data: schedules, isLoading, isFetching } = useQuery(
    [QK.visitTimeline, clientId, page, search, filter, status, location],
    () =>
      getVisitTimeline(
        page,
        clientId || "",
        "",
        location,
        "",
        fromDate,
        toDate,
        status,
        dateParam
      ),
    {
      enabled: !!clientId && open,
      staleTime: 30000,
    }
  );

  const totalRecords = schedules?.count || 0;
  const totalPages = Math.ceil(totalRecords / 30);

  const handlePageChange = (event, value) => setPage(value);

  const handleReset = () => {
    setSearch("");
    setFilter("today");
    setStatus("");
    setLocation("");
    setPage(1);
  };

  const handleDeleteJob = async () => {
    setDeleteLoading(true);
    const response = await deleteScheduleJob(jobId);
    if (response.status === 204) {
      setShowConfirmModal(false);
      setJobId(null);
      enqueueSnackbar("Job Deleted Successfully", { variant: "success" });
      queryClient.invalidateQueries(QK.visitTimeline);
    } else {
      enqueueSnackbar("Error Deleting Job", { variant: "error" });
    }
    setDeleteLoading(false);
  };

  const handleEditTimeModalOpen = (id, status) => {
    setShowEditTimeModal(true);
    setJobId(id);
    setJobStatus(status);
  };

  const handleJobDetailsModalOpen = (id) => {
    setShowJobDetailsModal(true);
    setJobId(id);
  };

  const handleConfirmModalOpen = (id) => {
    setShowConfirmModal(true);
    setJobId(id);
  };

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "0", label: "Pending" },
    { value: "1", label: "Scheduled" },
    { value: "2", label: "In Progress" },
    { value: "4", label: "Completed" },
  ];

  const clientName = schedules?.results?.[0]?.client_name || "";
  const showSkeleton = isLoading && !schedules;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 700, md: 850 },
            borderRadius: { xs: 0, sm: "16px 0 0 16px" },
            p: 3,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#FFFFFF",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.primary.main,
              }}
            >
              {clientName ? clientName.charAt(0).toUpperCase() : <PersonIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Day to Day Schedules
              </Typography>
              {clientName && (
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  for {clientName}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <ScheduleFilter
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
          status={status}
          setStatus={setStatus}
          onReset={handleReset}
          statusOptions={statusOptions}
        />

        {showSkeleton ? (
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <ScheduleFilterSkeleton />
            {Array.from({ length: 5 }).map((_, i) => (
              <ScheduleCardSkeleton key={i} theme={theme} />
            ))}
          </Box>
        ) : schedules?.results?.length > 0 ? (
          <>
            <ScheduleList
              schedules={schedules?.results}
              onView={handleJobDetailsModalOpen}
              onEditTime={handleEditTimeModalOpen}
              onDelete={handleConfirmModalOpen}
              isAdmin={profile?.user_type === "admin"}
              isLoading={isLoading}
            />

            {totalPages > 1 && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        ) : (
          <EmptyState />
        )}
      </Drawer>

      <JobEditModal
        show={showEditTimeModal}
        handleClose={() => {
          setShowEditTimeModal(false);
          setJobId(null);
          setJobStatus("");
        }}
        JobId={jobId}
        status={jobStatus}
      />

      <TaskDetailsModal
        show={showJobDetailsModal}
        handleClose={() => {
          setShowJobDetailsModal(false);
          setJobId(null);
        }}
        id={jobId}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => {
          setShowConfirmModal(false);
          setJobId(null);
        }}
        handleConfirm={handleDeleteJob}
        isLoading={deleteLoading}
        title="Delete Job"
        body="Are you sure you want to delete this job?"
        isDelete="true"
      />
    </>
  );
};

export default ClientSchedulesDrawer;